import { useContext, useEffect, useMemo, useState } from 'react';

import { useRouter, useStoreData } from '../../../../hooks';
import { CoreContext } from '../../../../providers';
import { setError } from '../../../../state';
import { Storyblok } from '../../../../storyblok';

export const usePageData = () => {
    const { store, settings } = useContext(CoreContext);
    const pagesData = useStoreData().storyblok.PageItems.items;

    const [currentPageData, setCurrentPageData] = useState<
        Storyblok.Content.Page | 'notFound'
    >(undefined!);

    const { pathname, location } = useRouter();

    /**
     * We can't fetch data for '/' slug so the homepage need an explicit
     * slug `home`. This slug matches Storyblok CMS's homepage slug.
     *
     * Every other page like /about should set this variable as `about`.
     *
     * Additonally, we do a `.replace(/\/+/g, '-').replace(/^-+/, '')` on
     * `match.url` to make sure we match correct Page from Storyblok while
     * we browse "nested" routes like /data/admin/:slug.
     * The url is replaced so `/data/example` will try to match Storyblok
     * `debt-admin-example` slug.
     */
    const slug = useMemo(
        () => (location.pathname === '/' ? '/home' : location.pathname),
        [location],
    );

    /**
     * On route change we would like to get rid of any error that is
     * currently displayed
     */
    useEffect(() => {
        store.dispatch(setError(null));
    }, [pathname]);

    /**
     * Set `currentPageData` by finding matching slug on fetched Storyblok
     * pages schema. Otherwise set `notFound`
     */
    useEffect(() => {
        if (pagesData) {
            return setCurrentPageData(
                pagesData.find(
                    (page: Storyblok.Content.Page) =>
                        page.full_slug.replace(
                            `${settings.languageVersion}/pages`,
                            '',
                        ) === slug,
                ) || 'notFound',
            );
        }

        setCurrentPageData('notFound');
    }, [pagesData, slug]);

    return { currentPageData, setCurrentPageData };
};
